import { useCallback, useEffect, useRef } from 'react';
import type { EntitlementDecision, PlanSet } from '@tabbio/types';
import { useUpgradeModal } from '../providers/useUpgradeModal';
import { chatPaywallTrigger, type ChatPaywallTrigger } from '../lib/chat-paywall';
import { createChatUpgradeContext } from '../lib/monetization-context';

type Activity = { toolName: string; result?: unknown };

function currentPath(): string | undefined {
  if (typeof window === 'undefined' || !window.location) return undefined;
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Opens the paywall from the chat stream itself (fair use, 2026-09-25):
 * at once when a send was refused for credits, and after the reply settles
 * when a tool inside it was refused, so the message in progress always
 * finishes first. At most once per reply; the inline card stays as the
 * fallback and reloaded threads never pop it.
 */
export function useChatPaywall(planSet: PlanSet) {
  const upgradeModal = useUpgradeModal();
  const openRef = useRef(upgradeModal.open);
  const pendingRef = useRef<ChatPaywallTrigger | null>(null);
  const openedThisReplyRef = useRef(false);

  useEffect(() => {
    openRef.current = upgradeModal.open;
  }, [upgradeModal.open]);

  const show = useCallback(
    (decision: EntitlementDecision, entryPoint: ChatPaywallTrigger['entryPoint']) => {
      if (openedThisReplyRef.current) return;
      openedThisReplyRef.current = true;
      try {
        openRef.current(createChatUpgradeContext({ decision, entryPoint, planSet, returnTo: currentPath() }));
      } catch {
        // The paywall must never break the chat stream.
      }
    },
    [planSet],
  );

  const onLiveToolResult = useCallback(
    (activity: Activity) => {
      const trigger = chatPaywallTrigger(activity);
      if (!trigger) return;
      if (trigger.timing === 'now') show(trigger.decision, trigger.entryPoint);
      else pendingRef.current ??= trigger;
    },
    [show],
  );

  /** Called at the reply's terminal frame. */
  const onReplySettled = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) show(pending.decision, pending.entryPoint);
    openedThisReplyRef.current = false;
  }, [show]);

  return { onLiveToolResult, onReplySettled };
}
